import { useCallback, useEffect, useRef } from "react"
import { eraseCookie } from "../../cookie"
import { useTranslation } from "react-i18next"
import Swal from "sweetalert2"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { clearUserData, editIsLoggedIn } from "../../redux/slices/userSlice"
import { useNavigate } from "react-router-dom"

type Props = {
  warningDuration?: number
  redirectDuration?: number
}

const IdleTimeCount = ({
  warningDuration = 540,
  redirectDuration = 1200,
}: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isLoggedIn } = useAppSelector((state) => state.user)
  const dispatch = useAppDispatch()
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const logout = useCallback(() => {
    eraseCookie()
    sessionStorage.removeItem("get_latest_form")
    sessionStorage.removeItem("selected_position")
    sessionStorage.removeItem("all_selected")
    sessionStorage.removeItem("id_card_number")
    dispatch(clearUserData())
    navigate(`/login`)
  }, [eraseCookie, dispatch])

  const clearAllTimeouts = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
      warningTimeoutRef.current = null
    }

    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current)
      redirectTimeoutRef.current = null
    }
  }, [])

  const startIdleCounting = useCallback(() => {
    if (!isLoggedIn) {
      return
    }

    //Clear the warning timeout once they active
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
    }

    warningTimeoutRef.current = setTimeout(() => {
      if (!isLoggedIn) {
        return
      }

      Swal.fire({
        html: `${t("Session warning prefix")} <b style="color: red">${
          (redirectDuration - warningDuration) / 60
        } ${t("minutes")}</b> ${t("Session warning suffix")}`,
        icon: "warning",
        width: "500",
        showCancelButton: true,
        confirmButtonText: `${t("Session Continue")}`,
        confirmButtonColor: "#43a047",
        cancelButtonText: `${t("Logout")}`,
        cancelButtonColor: "#ff0000",
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          //Clear the timeout if they click continue
          clearAllTimeouts()
          startIdleCounting()

          Swal.close()
        } else if (result.isDismissed && result.dismiss) {
          clearAllTimeouts()
          logout()
        }
      })

      //Counting idle time after the warning popup has been shown
      redirectTimeoutRef.current = setTimeout(() => {
        Swal.fire({
          text: `Session expired, ${t("Please login again")}`,
          icon: "error",
          width: "500",
          confirmButtonText: `${t("Go to login")}`,
          confirmButtonColor: "#43a047",
          allowOutsideClick: false,
        })
        logout()
      }, (redirectDuration - warningDuration) * 1000)
    }, warningDuration * 1000)
  }, [
    warningTimeoutRef,
    redirectTimeoutRef,
    isLoggedIn,
    clearAllTimeouts,
    logout,
  ])

  const handleUserMove = useCallback(() => {
    startIdleCounting()
  }, [startIdleCounting])

  useEffect(() => {
    // Initial setup: start the timer immediately
    startIdleCounting()

    // Attach event listeners for user activity
    window.addEventListener("mousemove", startIdleCounting)
    window.addEventListener("keydown", startIdleCounting)
    window.addEventListener("mousedown", startIdleCounting)
    window.addEventListener("scroll", startIdleCounting)
    window.addEventListener("touchstart", startIdleCounting) // For touch devices

    // Clean up event listeners and the timer when the component unmounts
    return () => {
      clearAllTimeouts()
      window.removeEventListener("mousemove", startIdleCounting)
      window.removeEventListener("keydown", startIdleCounting)
      window.removeEventListener("mousedown", startIdleCounting)
      window.removeEventListener("scroll", startIdleCounting)
      window.removeEventListener("touchstart", startIdleCounting)
    }
  }, [handleUserMove, startIdleCounting, clearAllTimeouts])

  return null
}

export default IdleTimeCount
